import { inject, reactive, shallowRef, toRefs, watchEffect } from 'vue';

import { IN_BROWSER, mergeDeep, SUPPORTS_TOUCH } from '@components/utils';

import type { InjectionKey, Ref } from 'vue';

export const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
export const breakpointsUpDown = ['smUp', 'smDown', 'mdUp', 'mdDown', 'lgUp', 'lgDown', 'xlUp', 'xlDown'] as const;
export type TBreakpoint = typeof breakpoints[number];
export type TBreakpointUpDown = typeof breakpointsUpDown[number];
export type TBreakpointMap = Record<TBreakpoint, string>;
const defaultDisplayOptions: IDisplayOptions = {
  mobileBreakpoint: 'sm',
  thresholds: {
    xs: 320,
    sm: 672,
    md: 1056,
    lg: 1312,
    xl: 1584
  }
};

export interface IDisplayOptions {
  mobileBreakpoint?: number | TBreakpoint;
  thresholds?: Partial<TDisplayThresholds>;
}
export interface IDisplayInstance {
  name: Ref<TBreakpoint>;
  xs: Ref<boolean>;
  sm: Ref<boolean>;
  md: Ref<boolean>;
  lg: Ref<boolean>;
  xl: Ref<boolean>;
  smUp: Ref<boolean>;
  mdUp: Ref<boolean>;
  lgUp: Ref<boolean>;
  xlUp: Ref<boolean>;
  smDown: Ref<boolean>;
  mdDown: Ref<boolean>;
  lgDown: Ref<boolean>;
  xlDown: Ref<boolean>;
  mobile: Ref<boolean>;
  mobileBreakpoint: Ref<number | TBreakpoint>;
  width: Ref<number>;
  height: Ref<number>;
  platform: Ref<TDisplayPlatform>;
  thresholds: Ref<TDisplayThresholds>;
  screenHeight: Ref<number>;
  screenWidth: Ref<number>;
  ssr: boolean;
  update(): void;
}
interface IInternalDisplayOptions {
  mobileBreakpoint: number | TBreakpoint;
  thresholds: TDisplayThresholds;
}

export type TDisplayThresholds = { [key in TBreakpoint]: number };
export type TDisplayPlatform = {
  android: boolean;
  ios: boolean;
  cordova: boolean;
  electron: boolean;
  chrome: boolean;
  edge: boolean;
  firefox: boolean;
  opera: boolean;
  win: boolean;
  mac: boolean;
  linux: boolean;
  touch: boolean;
  ssr: boolean;
};

export type TSSROptions =
  | boolean
  | {
      clientWidth: number;
      clientHeight?: number;
    };

export const DisplaySymbol: InjectionKey<IDisplayInstance> = Symbol.for('cds:display');

export function createDisplay(options?: IDisplayOptions, ssr?: TSSROptions): IDisplayInstance {
  const { thresholds, mobileBreakpoint } = parseDisplayOptions(options);

  const width = shallowRef(getClientWidth(ssr));
  const height = shallowRef(getClientHeight(ssr));
  const platform = shallowRef(getPlatform(ssr));

  const screenWidth = shallowRef(IN_BROWSER ? window.innerWidth : 0);
  const screenHeight = shallowRef(IN_BROWSER ? window.innerHeight : 0);

  const state = reactive<IDisplayInstance>({} as IDisplayInstance);

  watchEffect(() => {
    const xs = width.value < thresholds.xs;
    const sm = width.value < thresholds.md && !xs;
    const md = width.value < thresholds.lg && !(sm || xs);
    const lg = width.value < thresholds.xl && !(md || sm || xs);
    const xl = width.value >= thresholds.xl;

    const name = xs ? 'xs' : sm ? 'sm' : md ? 'md' : lg ? 'lg' : 'xl';
    const breakpointValue = typeof mobileBreakpoint === 'number' ? mobileBreakpoint : thresholds[mobileBreakpoint];
    const mobile = width.value < breakpointValue;

    state.xs = xs;
    state.sm = sm;
    state.md = md;
    state.lg = lg;
    state.xl = xl;
    state.smUp = !xs;
    state.mdUp = !(xs || sm);
    state.lgUp = !(xs || sm || md);
    state.xlUp = !(xs || sm || md || lg);
    state.smDown = !(md || lg || xl);
    state.mdDown = !(lg || xl);
    state.lgDown = !xl;
    state.name = name;
    state.height = height.value;
    state.width = width.value;
    state.mobile = mobile;
    state.mobileBreakpoint = mobileBreakpoint;
    state.platform = platform.value;
    state.thresholds = thresholds;
    state.screenWidth = screenWidth.value;
    state.screenHeight = screenHeight.value;
  });

  if (IN_BROWSER) {
    window.addEventListener('resize', updateSize, { passive: true });
  }

  function updateSize() {
    height.value = getClientHeight();
    width.value = getClientWidth();

    screenWidth.value = IN_BROWSER ? window.innerWidth : 0;

    screenHeight.value = IN_BROWSER ? window.innerHeight : 0;
  }

  function update() {
    updateSize();
    platform.value = getPlatform();
  }

  return { ...toRefs(state), update, ssr: !!ssr };
}

export function useDisplay(): IDisplayInstance {
  const display = inject(DisplaySymbol);

  if (!display) {
    throw new Error('Could not find `cds:display` injection key. Are you missing the createDisplay function?');
  }

  return display;
}

function parseDisplayOptions(options: IDisplayOptions = defaultDisplayOptions): IInternalDisplayOptions {
  return mergeDeep(defaultDisplayOptions, options) as IInternalDisplayOptions;
}

function getClientWidth(ssr?: TSSROptions) {
  return IN_BROWSER && !ssr ? window.innerWidth : (typeof ssr === 'object' && ssr.clientWidth) || 0;
}

function getClientHeight(ssr?: TSSROptions) {
  return IN_BROWSER && !ssr ? window.innerHeight : (typeof ssr === 'object' && ssr.clientHeight) || 0;
}

function getPlatform(ssr?: TSSROptions): TDisplayPlatform {
  const userAgent = IN_BROWSER && !ssr ? window.navigator.userAgent : 'ssr';

  function match(regexp: RegExp) {
    return Boolean(userAgent.match(regexp));
  }

  const android = match(/android/i);
  const ios = match(/iphone|ipad|ipod/i);
  const cordova = match(/cordova/i);
  const electron = match(/electron/i);
  const chrome = match(/chrome/i);
  const edge = match(/edge/i);
  const firefox = match(/firefox/i);
  const opera = match(/opera/i);
  const win = match(/win/i);
  const mac = match(/mac/i);
  const linux = match(/linux/i);

  return {
    android,
    ios,
    cordova,
    electron,
    chrome,
    edge,
    firefox,
    opera,
    win,
    mac,
    linux,
    touch: SUPPORTS_TOUCH,
    ssr: userAgent === 'ssr'
  };
}
